import { useState, useEffect, useCallback } from "react";
import { Minus, Plus, Save, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import { useCourseData } from "@/hooks/useCourseData";

export default function ScoreEditModal({ round, onClose, onSaved }) {
  const [editedScores, setEditedScores] = useState({});
  const [selectedHole, setSelectedHole] = useState(null);
  const [saving, setSaving] = useState(false);
  const { coursesData } = useCourseData();

  const course = round?.course || "Guadalajara Country Club";
  const HOLES = coursesData?.[course] || [];

  // Get played holes
  const playedHoles = [];
  if (round?.players?.length) {
    const allHoles = new Set();
    round.players.forEach(p => p.scores?.forEach(s => allHoles.add(s.hole_number)));
    playedHoles.push(...[...allHoles].sort((a, b) => a - b));
  }

  useEffect(() => {
    if (playedHoles.length && !selectedHole) {
      setSelectedHole(playedHoles[0]);
    }
  }, [playedHoles.length, selectedHole]);

  const getOriginalScore = useCallback((playerId, holeNumber) => {
    const player = round?.players?.find(p => p.player_id === playerId);
    const score = player?.scores?.find(s => s.hole_number === holeNumber);
    return score?.gross_score || 0;
  }, [round]);

  const getCurrentScore = (playerId, holeNumber) => {
    const key = `${playerId}_${holeNumber}`;
    if (editedScores[key] !== undefined) return editedScores[key];
    return getOriginalScore(playerId, holeNumber);
  };

  const setScore = (playerId, holeNumber, value) => {
    const key = `${playerId}_${holeNumber}`;
    const original = getOriginalScore(playerId, holeNumber);
    const clamped = Math.max(1, Math.min(15, value));
    setEditedScores(prev => {
      if (clamped === original) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { ...prev, [key]: clamped };
    });
  };

  const hasChanges = Object.keys(editedScores).length > 0;

  const handleSave = async () => {
    if (!hasChanges) return;
    setSaving(true);
    try {
      const edits = Object.entries(editedScores).map(([key, gross_score]) => {
        const [player_id, hole_number] = key.split("_");
        return { player_id, hole_number: parseInt(hole_number), gross_score };
      });

      const res = await fetch(`${API}/rounds/${round.round_id}/edit-scores`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ edits }),
      });

      if (res.ok) {
        onSaved?.();
        onClose();
      }
    } catch (err) {
      
    }
    setSaving(false);
  };

  if (!round || !HOLES.length || !playedHoles.length) return null;

  const holePar = HOLES[selectedHole - 1]?.par || 4;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div
        className="bg-white w-full max-w-md rounded-t-2xl sm:rounded-2xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="score-edit-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#0F172A]">Editar Scores</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Hole tabs */}
        <div className="flex gap-1 p-3 overflow-x-auto border-b border-gray-100 flex-shrink-0">
          {playedHoles.map(h => {
            const holeData = HOLES[h - 1];
            const hasEdit = round.players.some(p => editedScores[`${p.player_id}_${h}`] !== undefined);
            return (
              <button
                key={h}
                onClick={() => setSelectedHole(h)}
                data-testid={`hole-tab-${h}`}
                className={`flex-shrink-0 flex flex-col items-center px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  selectedHole === h
                    ? "bg-[#1B4D3E] text-white"
                    : hasEdit
                    ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                <span>{h}</span>
                <span className="text-[10px] font-normal opacity-70">P{holeData?.par}</span>
              </button>
            );
          })}
        </div>

        {/* Player scores for selected hole */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div className="text-center mb-2">
            <span className="text-sm text-gray-500">Hoyo {selectedHole}</span>
            <span className="mx-2 text-gray-300">|</span>
            <span className="text-sm font-semibold text-[#1B4D3E]">Par {holePar}</span>
          </div>

          {round.players.map(player => {
            const current = getCurrentScore(player.player_id, selectedHole);
            const original = getOriginalScore(player.player_id, selectedHole);
            const isEdited = editedScores[`${player.player_id}_${selectedHole}`] !== undefined;
            const diff = current - holePar;

            return (
              <div
                key={player.player_id}
                data-testid={`edit-player-${player.player_id}`}
                className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                  isEdited ? "bg-amber-50 ring-1 ring-amber-200" : "bg-gray-50"
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[#0F172A] truncate">{player.name}</p>
                  <p className="text-xs text-gray-400">HCP {player.handicap}</p>
                </div>

                <div className="flex items-center gap-2">
                  {isEdited && (
                    <span className="text-xs text-amber-600 font-medium">
                      era {original}
                    </span>
                  )}
                  <button
                    onClick={() => setScore(player.player_id, selectedHole, current - 1)}
                    className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                    data-testid={`score-minus-${player.player_id}`}
                  >
                    <Minus className="w-4 h-4 text-gray-600" />
                  </button>

                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                    diff <= -2 ? "bg-yellow-400 text-black" :
                    diff === -1 ? "bg-red-500 text-white" :
                    diff === 0 ? "bg-[#1B4D3E] text-white" :
                    diff === 1 ? "bg-gray-300 text-gray-700" :
                    "bg-gray-200 text-gray-500"
                  }`}>
                    {current}
                  </div>

                  <button
                    onClick={() => setScore(player.player_id, selectedHole, current + 1)}
                    className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                    data-testid={`score-plus-${player.player_id}`}
                  >
                    <Plus className="w-4 h-4 text-gray-600" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 flex gap-2">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            data-testid="save-edits-btn"
            className="flex-1 bg-[#1B4D3E] hover:bg-[#163F33] text-white font-bold"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Guardando..." : `Guardar ${Object.keys(editedScores).length} cambio${Object.keys(editedScores).length !== 1 ? "s" : ""}`}
          </Button>
          <Button onClick={onClose} variant="outline" className="text-gray-500">
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
}
